import { usePreferredLocaleContext } from "components/context/user/PreferredLocaleContext"
import { useCurrentUser } from "components/context/user/UserContext"
import { useFlags } from "launchdarkly-react-client-sdk"
import React from "react"
import { useIntl } from "react-intl"
import { reportErrorMessage } from "utils/ErrorReporting"
import { CountryCode, CountryCodes } from "content/CountryMap"

export enum SupportedLocale {
  English_UnitedStates = "en-US",
  Spanish_UnitedStates = "es-US",
  French_Canada = "fr-CA",
  German_Germany = "de-DE",
  Portuguese_Brazil = "pt-BR",
  Hindi_India = "hi-IN"
}

export enum PseudoLocale {
  /* pseudo-locales
    see https://formatjs.io/docs/tooling/cli/#--pseudo-locale-pseudolocale
    and https://github.com/formatjs/formatjs/blob/main/packages/cli-lib/src/pseudo_locale.ts
  */
  Elongated = "xx-LS",
  Accented = "en-XA",
  Bidirectional = "en-XB"
}

const pseudoLocales: Array<PseudoLocale> = Object.values(PseudoLocale)

function isPseudoLocale(locale: SupportedLocale | PseudoLocale): locale is PseudoLocale {
  return pseudoLocales.includes(locale as PseudoLocale)
}

const displayNamesBySupportedLocale = {
  [SupportedLocale.English_UnitedStates]: "English (U.S.)",
  [SupportedLocale.Spanish_UnitedStates]: "Español (EE. UU.)",
  [SupportedLocale.French_Canada]: "Français (Canada)",
  [SupportedLocale.German_Germany]: "Deutsch (Deutschland)",
  [SupportedLocale.Portuguese_Brazil]: "Português (Brasil)",
  [SupportedLocale.Hindi_India]: "हिन्दी (भारत)"
} as const

export const defaultLocale = SupportedLocale.English_UnitedStates

export function getSupportedLocaleDisplayName(locale: SupportedLocale): string {
  return displayNamesBySupportedLocale[locale]
}

type CompiledMessage = {
  type: number
  value: string
}

type MessageId = string
type CompiledMessages = Record<MessageId, CompiledMessage[]>

// TODO add tests for this
function resolveLocaleMessageFile(
  locale: SupportedLocale | PseudoLocale,
  allowPseudoLocales: boolean
): `${string}.json` {
  if (allowPseudoLocales && isPseudoLocale(locale)) {
    return `${locale}.json`
  }

  switch (locale) {
    case SupportedLocale.Spanish_UnitedStates:
      return "es-US.json"
    case SupportedLocale.French_Canada:
      return "fr-CA.json"
    case SupportedLocale.German_Germany:
      return "de-DE.json"
    case SupportedLocale.Portuguese_Brazil:
      return "pt-BR.json"
    case SupportedLocale.Hindi_India:
      return "hi-IN.json"
    case SupportedLocale.English_UnitedStates:
    default:
      return "en-US.json"
  }
}

function loadLocaleMessages(locale: SupportedLocale, allowPseudoLocales: boolean): Promise<CompiledMessages> {
  const messageFile = resolveLocaleMessageFile(locale, allowPseudoLocales)
  return import(`compiledLang/${messageFile}`)
}

export function useIntlMessages(): { didCompiledMessagesLoad: boolean } {
  const intl = useIntl()
  const locale: SupportedLocale = intl.locale as SupportedLocale
  const { setCompiledMessages } = usePreferredLocaleContext()
  const currentUser = useCurrentUser()
  const [didCompiledMessagesLoad, setDidCompiledMessagesLoad] = React.useState(false)

  React.useEffect(() => {
    async function getLocaleData(): Promise<void> {
      try {
        const allowPseudoLocales = currentUser?.isTestUser
        const messages = await loadLocaleMessages(locale, allowPseudoLocales)

        setCompiledMessages(messages)
        setDidCompiledMessagesLoad(true)
      } catch (err) {
        reportErrorMessage("Could not load locale messages")
      }
    }
    getLocaleData()
  }, [locale, setCompiledMessages, currentUser])

  return { didCompiledMessagesLoad }
}

/*****************************************************************************
 * The following section is utility functions and hooks that implement
 * the country code to locale configuration defined in launch darkly
 *
 * https://app.launchdarkly.com/default/test/features/supported-locales/variations
 *
 ******************************************************************************/

/********* Utility functions/types *********/

function isValidCountryCode(country: string): country is CountryCode {
  return CountryCodes.includes(country as CountryCode)
}

export function isValidLocale(locale: string): locale is SupportedLocale {
  const supportedLocales = Object.values(SupportedLocale)

  return supportedLocales.includes(locale as SupportedLocale)
}

type CountryCodeToLocaleMap = Partial<Record<CountryCode, SupportedLocale[]>>

/********* Hooks *********/

/**
 * Validates the launch darkly configuration to protect against
 * user errors.
 *
 * For example:
 * 1. adding a country code that is not supported
 * 2. adding a country code with no preferred locales
 * 3. adding a preferred locale that is not yet supported by the rest of the app
 * 4. adding a single preferred locale string instead of a list of preferred locales
 */
export const useValidatedSupportedLocales = (): CountryCodeToLocaleMap => {
  const { supportedLocales: configuredLocales }: { supportedLocales: CountryCodeToLocaleMap } = useFlags()

  const countryCodeToLocaleMap = {}

  if (!configuredLocales || Object.keys(configuredLocales).length === 0) {
    return countryCodeToLocaleMap
  }

  const countryCodes = Object.keys(configuredLocales)

  countryCodes.forEach((countryCode) => {
    if (isValidCountryCode(countryCode)) {
      const isLocaleList = Array.isArray(configuredLocales[countryCode])
      const hasLocales = configuredLocales[countryCode].length > 0

      if (hasLocales && isLocaleList) {
        const locales = configuredLocales[countryCode].filter(isValidLocale)

        // @ts-expect-error TS7053
        countryCodeToLocaleMap[countryCode] = locales
      }
    }
  })

  return countryCodeToLocaleMap
}

/**
 * Returns list of locales for a given country
 * based on launch darkly configuration
 */
export const useSupportedLocalesForCountry = (): ReadonlyArray<SupportedLocale> => {
  const { company } = useCurrentUser()
  const countryCode: CountryCode = company?.countryCode

  const countryCodeToLocaleMap = useValidatedSupportedLocales()
  const locales = countryCodeToLocaleMap[countryCode] ?? []

  if (!locales.includes(SupportedLocale.English_UnitedStates)) {
    return [SupportedLocale.English_UnitedStates, ...locales]
  }

  return locales
}

/**
 * Returns a list of all possible locales for all countries
 * based on launch darkly configuration
 */
export const useAllLocales = (): ReadonlyArray<SupportedLocale> => {
  const countryCodeToLocaleMap = useValidatedSupportedLocales()
  const allSupportedLocales = Object.values(SupportedLocale)
  const allSupportedlocalesForAllCountries = Object.values(countryCodeToLocaleMap).flat()

  return allSupportedLocales.filter(
    (locale) => locale === SupportedLocale.English_UnitedStates || allSupportedlocalesForAllCountries.includes(locale)
  )
}
